
import Vue from "vue";
import { Component } from "vue-property-decorator";
import { globalStore, uiStore } from "@/store/store";

import SelectorsList from "./SelectorsList.vue";
import PanelHeader from "@/components/Reusable/Util/PanelHeader.vue";
import BrowseModeToggle from "@/components/BrowseMode/BrowseModeToggle.vue";
import PanelHeaderButton from "@/components/Reusable/Util/PanelHeaderButton.vue";
import AlertBox from "@/components/Reusable/CustomInputs/AlertBox.vue";
import FreeModeAlertForSelectors from "@/components/Subscription/FreeModeAlertForSelectors.vue";
import ClassProperties from "@/oxygen/classes/ClassProperties.vue";
import RevisionsPanel from "@/components/Settings/RevisionsPanel.vue";
import SelectorsPanelHistoryViewMode from "@/oxygen/classes/panel/SelectorsPanelHistoryViewMode.vue";
import AddSelector from "@/oxygen/classes/panel/AddSelector.vue";
import IconButton from "@/components/UI/IconButton.vue";
import { closeUiPanel } from "@/lib/panels/panels";
import ImportExportDropdownMenu from "@/components/Settings/DocumentOrGlobalSettings/ImportExportDropdownMenu.vue";
import NotificationLogger from "@/lib/notifications/notifications";
import { pipe } from "fp-ts/function";
import * as t from "io-ts";
import { fold } from "fp-ts/Either";
import { breakdanceUndo } from "@/lib/undo/undo";
import { OxySelector, OxySelectorV } from "@/oxygen/classes/lib/types";
import { mergeSelectors } from "@/oxygen/classes/lib/importer";
import { DEFAULT_NEW_COLLECTION } from "@/oxygen/classes/lib/classes";
import { iotsErrorsToCustomErrorType } from "@/util/io-ts-errors-to-_error";

@Component({
  components: {
    ImportExportDropdownMenu,
    AddSelector,
    AlertBox,
    PanelHeader,
    SelectorsList,
    RevisionsPanel,
    ClassProperties,
    BrowseModeToggle,
    PanelHeaderButton,
    FreeModeAlertForSelectors,
    SelectorsPanelHistoryViewMode,
    IconButton
  }
})
export default class OxySelectorsPanel extends Vue {
  selectorId: string | null = null;
  nestedId: string | null = null;

  get selector() {
    return this.selectors.find(selector => selector.id === this.selectorId);
  }

  get nestedSelector() {
    return this.selector?.children?.find(child => child.id === this.nestedId);
  }

  get viewMode() {
    return uiStore.panelState.selectorsPanelPanelActiveViewMode;
  }

  get selectors() {
    return globalStore.oxySelectors;
  }

  get selectorName() {
    if (!this.selector) return null;

    if (this.nestedSelector) {
      return `.${this.selector.name} ${this.nestedSelector.name}`;
    }

    return this.selector.type === "class"
      ? `.${this.selector.name}`
      : this.selector.name;
  }

  get collections() {
    return globalStore.oxySelectorsCollections;
  }

  toggleViewMode() {
    uiStore.setPanelState({
      ...uiStore.panelState,
      selectorsPanelPanelActiveViewMode:
        this.viewMode === "controls" ? "history" : "controls"
    });
  }

  select(id: string) {
    this.selectorId = id;
  }

  selectNested(id: string, nestedId: string) {
    this.selectorId = id;
    this.nestedId = nestedId;
  }

  close() {
    if (this.selector) {
      this.selectorId = null;
      this.nestedId = null;
    } else {
      closeUiPanel("oxySelectors");
    }
  }

  validateImport(settingsToImport: Record<string, unknown>) {
    return pipe(
      settingsToImport,
      t.array(OxySelectorV).decode,
      fold(
        e => {
          NotificationLogger.log.errorMessage(
            "Error importing Selectors - the file is invalid."
          );
          iotsErrorsToCustomErrorType(e);
          return false;
        },
        () => {
          return true;
        }
      )
    );
  }

  importSelectors(selectorsToImport: OxySelector[], mode: "merge" | "replace") {
    if (!selectorsToImport.length) {
      NotificationLogger.log.errorMessage(
        "Error importing Selectors - the file is empty."
      );
      return;
    }

    breakdanceUndo.transaction(() => {
      if (mode === "replace") {
        // All the existing selectors are deleted and replaced with the new ones
        globalStore.setOxygenSelectors(selectorsToImport);

        NotificationLogger.log.message(
          `${selectorsToImport.length} selectors imported. All existing selectors were replaced.`
        );
      } else {
        // Merge and overwrite existing selectors by class name

        const { selectorsToKeep, selectors } = mergeSelectors(
          globalStore.oxySelectors,
          selectorsToImport
        );

        globalStore.setOxygenSelectors(selectors);

        NotificationLogger.log.message(
          `${selectorsToImport.length} selectors imported. ${selectorsToKeep.length} existing selectors were kept.`
        );
      }
    }, "Import Selectors");
  }

  addCollection() {
    const newCollection = DEFAULT_NEW_COLLECTION;
    const existingCollections = this.collections;

    let count = 1;
    while (existingCollections.includes(`${newCollection} ${count}`)) {
      count++;
    }

    const finalCollectionName = `${newCollection} ${count}`;

    globalStore.setOxygenSelectorsCollections([
      ...existingCollections,
      finalCollectionName
    ]);
  }
}
